import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  integrationCalls,
  type IntegrationCall,
  type IntegrationCallFilter,
  type IntegrationCallStatus,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';

import { CallDetailModal, CallStatusBadge, ResendDialog } from './IntegrationCallParts';
import { identificacao, opcoesDaSituacao } from './rotulosDoRegistro';

/**
 * Aba Registro de /integracoes: a "Consulta integracoes" do OnPoint.
 *
 * Lista as chamadas aos sistemas externos que nao sao consulta a bureau (essas tem
 * tela propria), com Identificacao, Tipo e Data da integracao, Filtros e Reenviar.
 */

const LIMITE = 25;

interface Filtros {
  from: string;
  to: string;
  call_type: string;
  status: string;
  provider_key: string;
  q: string;
}

const filtrosVazios: Filtros = {
  from: '',
  to: '',
  call_type: '',
  status: '',
  provider_key: '',
  q: '',
};

export function IntegrationCallsTab({ canResend }: { canResend: boolean }) {
  const [filtros, setFiltros] = useState<Filtros>(filtrosVazios);
  const [abrindoFiltros, setAbrindoFiltros] = useState(false);
  const [offset, setOffset] = useState(0);
  const [detalhe, setDetalhe] = useState<string | null>(null);
  const [reenviando, setReenviando] = useState<IntegrationCall | null>(null);

  const tipos = useQuery({
    queryKey: ['integration-call-types'],
    queryFn: ({ signal }) => integrationCalls.types(signal),
  });
  const tiposSemBureau = (tipos.data ?? []).filter((tipo) => tipo.kind !== 'bureau');

  const parametros: IntegrationCallFilter = {
    limit: LIMITE,
    offset,
    from: filtros.from,
    to: filtros.to,
    call_type: filtros.call_type,
    provider_key: filtros.provider_key,
    q: filtros.q,
  };
  if (filtros.status) parametros.status = filtros.status as IntegrationCallStatus;

  const consulta = useQuery({
    queryKey: ['integration-calls', 'registro', parametros],
    queryFn: ({ signal }) => integrationCalls.list(parametros, signal),
    placeholderData: keepPreviousData,
  });

  const colunas: Column<IntegrationCall>[] = [
    {
      // A coluna sem titulo do OnPoint, a esquerda da linha: aqui, a situacao.
      key: 'situacao',
      header: '',
      render: (call) => <CallStatusBadge call={call} />,
    },
    {
      key: 'identificacao',
      header: 'Identificacao da integracao',
      render: (call) => (
        <div className="flex flex-col">
          <span className="text-ink-900">{identificacao(call)}</span>
          {call.message ? (
            <span className="text-2xs text-ink-500">{call.message}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo da integracao',
      render: (call) => call.call_type_name,
    },
    {
      key: 'provedor',
      header: 'Provedor',
      hideBelow: 'md',
      render: (call) => call.provider_name,
    },
    {
      key: 'data',
      header: 'Data da integracao',
      render: (call) => formatDateTime(call.started_at),
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (call) => (
        <span className="inline-flex gap-1">
          <AcaoComIcone
            acao="visualizar"
            rotulo="Ver detalhes"
            onClick={() => setDetalhe(call.id)}
          />
          {canResend && call.can_resend ? (
            <AcaoComIcone
              acao="reenviar"
              rotulo="Reenviar"
              onClick={() => setReenviando(call)}
            />
          ) : null}
        </span>
      ),
    },
  ];

  const filtrando = Object.values(filtros).some(Boolean);
  const pagina = consulta.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-600">
          Chamadas aos sistemas externos, da mais recente para a mais antiga. As
          consultas a bureau ficam na tela Consultas a bureau.
        </p>
        <span className="inline-flex gap-2">
          {filtrando ? (
            <Button
              variant="secondary"
              onClick={() => {
                setFiltros(filtrosVazios);
                setOffset(0);
              }}
            >
              Limpar filtro
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => setAbrindoFiltros(true)}>
            Filtros
          </Button>
        </span>
      </div>

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar o registro">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <DataTable
          columns={colunas}
          items={pagina?.items ?? []}
          keyOf={(call) => call.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="plug"
              title="Nenhuma integracao registrada"
              description={
                filtrando
                  ? 'Nenhuma integracao atende aos filtros.'
                  : 'O registro mostra cada chamada a sistema externo, como a verificacao de provedor. As integracoes com o sistema da seguradora ainda nao tem adaptador.'
              }
            />
          }
        />
        {pagina ? (
          <Pagination
            total={pagina.total}
            limit={pagina.limit}
            offset={pagina.offset}
            onChange={setOffset}
          />
        ) : null}
      </Card>

      {abrindoFiltros ? (
        <FiltrosDoRegistro
          atuais={filtros}
          tipos={tiposSemBureau.map((tipo) => ({ value: tipo.key, label: tipo.name }))}
          onClose={() => setAbrindoFiltros(false)}
          onApply={(novos) => {
            setFiltros(novos);
            setOffset(0);
            setAbrindoFiltros(false);
          }}
        />
      ) : null}

      {detalhe ? (
        <CallDetailModal
          callId={detalhe}
          onClose={() => setDetalhe(null)}
          nomeOficial
        />
      ) : null}

      {reenviando ? (
        <ResendDialog
          call={reenviando}
          costConfirmation={
            tipos.data?.find((tipo) => tipo.key === reenviando.call_type)
              ?.cost_confirmation ?? false
          }
          title="Reenviar"
          onClose={() => setReenviando(null)}
        />
      ) : null}
    </div>
  );
}

function FiltrosDoRegistro({
  atuais,
  tipos,
  onClose,
  onApply,
}: {
  atuais: Filtros;
  tipos: { value: string; label: string }[];
  onClose: () => void;
  onApply: (filtros: Filtros) => void;
}) {
  const [rascunho, setRascunho] = useState<Filtros>(atuais);
  const muda = (campo: keyof Filtros) => (valor: string) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }));

  return (
    <Modal
      open
      onClose={onClose}
      title="Filtros"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => setRascunho(filtrosVazios)}>
            Limpar filtro
          </Button>
          <Button onClick={() => onApply(rascunho)}>Aplicar filtro</Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Data inicial"
          type="date"
          value={rascunho.from}
          onChange={(event) => muda('from')(event.target.value)}
        />
        <Field
          label="Data final"
          type="date"
          value={rascunho.to}
          onChange={(event) => muda('to')(event.target.value)}
        />
        <Select
          label="Tipo da integracao"
          placeholder="Todos"
          options={tipos}
          value={rascunho.call_type}
          onChange={(event) => muda('call_type')(event.target.value)}
        />
        <Select
          label="Situacao"
          placeholder="Todas"
          options={opcoesDaSituacao}
          value={rascunho.status}
          onChange={(event) => muda('status')(event.target.value)}
        />
        <Field
          label="Provedor"
          value={rascunho.provider_key}
          onChange={(event) => muda('provider_key')(event.target.value.trim())}
        />
        <Field
          label="Identificacao"
          value={rascunho.q}
          onChange={(event) => muda('q')(event.target.value)}
        />
      </div>
    </Modal>
  );
}
